interface Particle { x: number; y: number; z: number }

const PARTICLES: Particle[] = Array.from({ length: 78 }, (_, index) => {
  const y = 1 - (index / 77) * 2;
  const radius = Math.sqrt(1 - y * y);
  const angle = index * 2.3999632297;
  return { x: Math.cos(angle) * radius, y, z: Math.sin(angle) * radius };
});

function project(particle: Particle, time: number, radius: number, cx: number, cy: number): Particle {
  const rotation = time * 0.15;
  const tilt = 0.35 + Math.sin(time * 0.13) * 0.15;
  const x = particle.x * Math.cos(rotation) + particle.z * Math.sin(rotation);
  const z = -particle.x * Math.sin(rotation) + particle.z * Math.cos(rotation);
  return {
    x: cx + x * radius,
    y: cy + (particle.y * Math.cos(tilt) - z * Math.sin(tilt)) * radius,
    z: particle.y * Math.sin(tilt) + z * Math.cos(tilt),
  };
}

export function drawConsultantThinkingNucleus(
  context: CanvasRenderingContext2D, width: number, height: number, time: number, color: string,
): void {
  const cx = width / 2;
  const cy = height / 2 + 1;
  const radius = Math.min(54 + Math.sin(time * 0.85) * 2, height * 0.36, width * 0.28);
  context.clearRect(0, 0, width, height);
  context.save();
  const glow = context.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.5);
  glow.addColorStop(0, color);
  glow.addColorStop(1, 'transparent');
  context.globalAlpha = 0.085;
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  const points = PARTICLES.map((particle) => project(particle, time, radius, cx, cy));
  context.strokeStyle = color;
  context.fillStyle = color;
  context.lineWidth = 0.65;
  for (let index = 0; index < points.length; index += 1) {
    for (let other = index + 1; other < points.length; other += 1) {
      const a = points[index];
      const b = points[other];
      const distance = Math.hypot(a.x - b.x, a.y - b.y, (a.z - b.z) * radius);
      if (distance > 24) continue;
      context.globalAlpha = (1 - distance / 30) * (0.15 + (a.z + 1) * 0.13);
      context.beginPath();
      context.moveTo(a.x, a.y);
      context.lineTo(b.x, b.y);
      context.stroke();
    }
  }

  for (let orbit = 0; orbit < 3; orbit += 1) {
    const tilt = orbit * Math.PI / 3 + 0.25;
    const rx = radius * 1.22;
    const ry = radius * 0.3;
    context.globalAlpha = 0.16;
    context.lineWidth = 0.6;
    context.beginPath();
    context.ellipse(cx, cy, rx, ry, tilt, 0, Math.PI * 2);
    context.stroke();
    const angle = time * (0.55 + orbit * 0.14) * (orbit === 1 ? -1 : 1) + orbit * 2;
    const ex = Math.cos(angle) * rx;
    const ey = Math.sin(angle) * ry;
    const x = cx + ex * Math.cos(tilt) - ey * Math.sin(tilt);
    const y = cy + ex * Math.sin(tilt) + ey * Math.cos(tilt);
    context.globalAlpha = 0.85;
    context.shadowColor = color;
    context.shadowBlur = 8;
    context.beginPath();
    context.arc(x, y, 1.5, 0, Math.PI * 2);
    context.fill();
    context.shadowBlur = 0;
  }
  for (const point of points) {
    context.globalAlpha = 0.22 + (point.z + 1) * 0.28;
    context.beginPath();
    context.arc(point.x, point.y, 0.65 + (point.z + 1) * 0.35, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
}
